import {
  IonButton,
  IonItem,
  IonList,
  IonPopover,
} from '@ionic/react';
import { ActionIcon, Group, Menu } from '@mantine/core';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EnFlagIcon from '../icons/en-flag';
import FrFlagIcon from '../icons/fr-flag';

import styles from './lang-select.module.css';

interface Item {
  value: 'fr' | 'en';
  label: string;
}

const icons = {
  fr: (
    <FrFlagIcon
      className={styles['flag-icon']}
      style={{ width: '100%', height: '100%' }}
    />
  ),
  en: (
    <EnFlagIcon
      className={styles['flag-icon']}
      style={{ width: '100%', height: '100%' }}
    />
  ),
};

const data: Item[] = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
];

function normalizeLocale(language: string | undefined): 'fr' | 'en' {
  if (!language) {
    return 'fr';
  }
  const code = language.split('-')[0];
  return code === 'en' ? 'en' : 'fr';
}

export interface LangSelectProps {
  /** Mantine menu for desktop; Ionic popover for mobile header (works inside IonButtons). */
  variant?: 'mantine' | 'ionic';
  /** Extra class on the Ionic trigger button (e.g. mobile header toolbar sizing). */
  ionicButtonClassName?: string;
}

function useLangSelectState() {
  const { i18n } = useTranslation([], { useSuspense: false });
  const [value, setValue] = useState<'fr' | 'en'>(() =>
    normalizeLocale(i18n.language),
  );

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setValue(normalizeLocale(lng));
    };

    i18n.on('languageChanged', onLanguageChanged);
    if (i18n.isInitialized && i18n.language) {
      setValue(normalizeLocale(i18n.language));
    }

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = (next: 'fr' | 'en') => {
    if (next === value) {
      return;
    }
    setValue(next);
    void i18n.changeLanguage(next);
  };

  const selectedOption = data.find((item) => item.value === value);
  const otherOptions = data.filter((item) => item.value !== value);

  return { value, changeLanguage, selectedOption, otherOptions };
}

function LangSelectMantine() {
  const { changeLanguage, selectedOption, otherOptions } = useLangSelectState();

  const options = otherOptions.map((item) => (
    <Menu.Item
      className={styles['option']}
      key={item.value}
      aria-label={item.label}
      onClick={() => changeLanguage(item.value)}
    >
      <div className={styles['option-inner']}>
        <Group>{icons[item.value]}</Group>
      </div>
    </Menu.Item>
  ));

  return (
    <Menu
      classNames={{ dropdown: styles.dropdown }}
      position="bottom-end"
      withinPortal
      zIndex={10000}
    >
      <Menu.Target>
        {selectedOption ? (
          <ActionIcon
            aria-label={selectedOption.label}
            className={styles['flag-action-icon']}
            radius="xl"
            size="lg"
            variant="default"
          >
            {icons[selectedOption.value]}
          </ActionIcon>
        ) : (
          <div />
        )}
      </Menu.Target>
      <Menu.Dropdown>{options}</Menu.Dropdown>
    </Menu>
  );
}

function LangSelectIonic({
  ionicButtonClassName,
}: {
  ionicButtonClassName?: string;
}) {
  const { changeLanguage, selectedOption, otherOptions } = useLangSelectState();
  const triggerId = useId().replace(/:/g, '');
  const popoverRef = useRef<HTMLIonPopoverElement>(null);

  const handleLanguageChange = async (next: 'fr' | 'en') => {
    changeLanguage(next);
    await popoverRef.current?.dismiss();
  };

  return (
    <>
      <IonButton
        id={triggerId}
        aria-label={selectedOption?.label}
        fill="clear"
        className={[styles['ionic-trigger'], ionicButtonClassName]
          .filter(Boolean)
          .join(' ')}
      >
        {selectedOption ? (
          <span className={styles['flag-action-icon']}>
            {icons[selectedOption.value]}
          </span>
        ) : null}
      </IonButton>
      <IonPopover
        ref={popoverRef}
        trigger={triggerId}
        dismissOnSelect
        side="bottom"
        alignment="end"
        className={styles['ionic-popover']}
      >
        <IonList lines="none">
          {otherOptions.map((item) => (
            <IonItem
              key={item.value}
              button
              aria-label={item.label}
              className={styles['option']}
              onClick={() => void handleLanguageChange(item.value)}
            >
              <div className={styles['option-inner']}>{icons[item.value]}</div>
            </IonItem>
          ))}
        </IonList>
      </IonPopover>
    </>
  );
}

export function LangSelect({
  variant = 'mantine',
  ionicButtonClassName,
}: LangSelectProps) {
  return variant === 'ionic' ? (
    <LangSelectIonic ionicButtonClassName={ionicButtonClassName} />
  ) : (
    <LangSelectMantine />
  );
}

export default LangSelect;
